import { useEffect, useState, useSyncExternalStore } from 'react';
import { Download, Share, Plus, MoreVertical, MonitorDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenantName} from '@/hooks/useTenantName';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getInstallPrompt,
  clearInstallPrompt,
  subscribeInstallPrompt,
} from '@/lib/offline/pwa-install';

type Platform = 'ios' | 'android' | 'desktop';

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS legacy standalone flag
  const nav = window.navigator as unknown as { standalone?: boolean };
  return Boolean(nav.standalone);
}

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as Macintosh but is touch-capable.
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && 'ontouchend' in document);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export function InstallPWAPrompt() {
  const tenantName = useTenantName();
  const deferred = useSyncExternalStore(
    subscribeInstallPrompt,
    getInstallPrompt,
    () => null,
  );
  const [installed, setInstalled] = useState<boolean>(detectStandalone);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onInstalled = () => {
      setInstalled(true);
      setHelpOpen(false);
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      // Native browser install confirmation, whose copy comes from the manifest
      // and stays platform-branded until that is generated per tenant.
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === 'accepted') clearInstallPrompt();
      return;
    }
    // No native prompt available → show manual instructions.
    setHelpOpen(true);
  };

  const platform = detectPlatform();

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 text-xs"
        onClick={() => {
          void handleClick();
        }}
      >
        <Download className="size-3.5" />
        Install app
      </Button>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md flex flex-col max-h-[calc(100dvh-2rem)] p-0">
          <DialogHeader className="shrink-0 px-6 pt-6">
            <DialogTitle className="flex items-center gap-2">
              <MonitorDown className="size-5 text-primary" /> Install {tenantName}
            </DialogTitle>
            <DialogDescription>
              Add {tenantName} to your device for a full-screen, app-like experience
              that also works offline.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 px-6 pt-4 pb-6 text-sm">
            {platform === 'ios' && <IosSteps />}
            {platform === 'android' && <AndroidSteps />}
            {platform === 'desktop' && <DesktopSteps />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
        {n}
      </span>
      <span className="pt-0.5 text-text-body">{children}</span>
    </li>
  );
}

function IosSteps() {
  return (
    <ol className="space-y-3">
      <Step n={1}>
        Open this site in <span className="font-medium text-text-heading">Safari</span> (other iOS browsers can&apos;t install apps).
      </Step>
      <Step n={2}>
        Tap the <span className="inline-flex items-center gap-1 font-medium text-text-heading"><Share className="size-4" /> Share</span> icon in the toolbar.
      </Step>
      <Step n={3}>
        Choose <span className="inline-flex items-center gap-1 font-medium text-text-heading"><Plus className="size-4" /> Add to Home Screen</span>, then tap <span className="font-medium text-text-heading">Add</span>.
      </Step>
    </ol>
  );
}

function AndroidSteps() {
  const tenantName = useTenantName();
  return (
    <ol className="space-y-3">
      <Step n={1}>
        Tap the <span className="inline-flex items-center gap-1 font-medium text-text-heading"><MoreVertical className="size-4" /> menu</span> in the top-right of your browser.
      </Step>
      <Step n={2}>
        Choose <span className="font-medium text-text-heading">Install app</span> (or <span className="font-medium text-text-heading">Add to Home screen</span>).
      </Step>
      <Step n={3}>Confirm, and {tenantName} will appear with your other apps.</Step>
    </ol>
  );
}

function DesktopSteps() {
  const tenantName = useTenantName();
  return (
    <ol className="space-y-3">
      <Step n={1}>
        Use <span className="font-medium text-text-heading">Chrome</span> or <span className="font-medium text-text-heading">Edge</span> (most other desktop browsers can&apos;t install web apps).
      </Step>
      <Step n={2}>
        Click the <span className="inline-flex items-center gap-1 font-medium text-text-heading"><MonitorDown className="size-4" /> install</span> icon at the right edge of the address bar — or open the browser menu and choose <span className="font-medium text-text-heading">Install {tenantName}</span>.
      </Step>
      <Step n={3}>Confirm, and it opens in its own window like a native app.</Step>
    </ol>
  );
}
